import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Subject, filter, skip, takeUntil, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
// Actions
import {
  loadFormsList,
  getSelectedForm,
  deleteForm,
  getSelectedFormSuccess,
  newForm
} from '../../../infrastructure/store/actions/form.actions';
import {
  getSelectedVersionSuccess,
  loadVersionsListSuccess
} from '../../../infrastructure/store/actions/version.actions';
import {
  getSelectedTemplateSuccess,
  loadTemplatesListSuccess,
  loadTemplatesList
} from '../../../infrastructure/store/actions/template.actions';

// Selectors
import {
  setForms,
  setSelectedForm,
  setNewForm,
  setListStatus as setListStatusForm,
  setGetStatus as setGetStatusForm,
  setCreateStatus as setCreateStatusForm,
  setDeleteStatus as setDeleteStatusForm,
  setUpdateStatus as setUpdateStatusForm,
  setCloneStatus as setCloneStatusForm,
  setCloneStatus
} from '../../../infrastructure/store/selectors/form.selector';
// Selectors
import {  
  setSelectedVersion,
  setVersions,
  setListStatus as setListStatusVersion,
  setGetStatus as setGetStatusVersion,
  setCreateStatus as setCreateStatusVersion,
  setDeleteStatus as setDeleteStatusVersion,
  setUpdateStatus as setUpdateStatusVersion
} from '../../../infrastructure/store/selectors/version.selector';
import {  
  setSelectedTemplate,
  setTemplates,
  setListStatus as setListStatusTemplate,
  setGetStatus as setGetStatusTemplate,
  setCreateStatus as setCreateStatusTemplate,
  setDeleteStatus as setDeleteStatusTemplate,
  setUpdateStatus as setUpdateStatusTemplate
} from '../../../infrastructure/store/selectors/template.selector';
// Selectors
import {  
  setListStatus as setListStatusValue,
  setGetStatus as setGetStatusValue
} from '../../../infrastructure/store/selectors/value.selector';
// Models
import { Form } from '../../../domain/form.interface';
import { Tabs } from '../../../domain/tabs.interface';
import { ConfirmationDialogData, ModalConfirmationComponent } from '../../commons/modal-confirmation/modal-confirmation.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  unsubscribe$ = new Subject();
  changes = false;
  loading = true; // General loading for all status
  loadingListForm = true;
  loadingGetForm = true;
  loadingDeleteForm = true;
  loadingCreateForm = true;
  loadingUpdateForm = true;
  loadingCreateVersion = true;
  loadingDeleteVersion = true;
  loadingUpdateVersion = true;
  loadingListValue = true;
  loadingGetValue = true;
  loadingCloneForm = true;


  elementSelected!: Form; // Element selected to toggle form-version

  forms: Form[] = []; // Forms lis
  formSelected!: Form; // Form selected
  versions: Form[] = [];
  versionSelected!: Form; // Version selected
  versionSelectedId: string; // Control version selector id data binding
   
  search: string = '';
  isNewForm = false; // Control new form status
  tabs: Tabs[] = [
    { label: 'General', value: '1' },
    { label: 'Fields', value: '2' },
    { label: 'Layout', value: '3' },
    { label: 'Templates', value: '5' },
    { label: 'Data', value: '4' },
  ];
  viewSelected: string = '1';
  
  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {    
    this.initForms();
    this.initLoadingStatus();
  }

  selectVersion() { 
    let selectedVersion = {...this.versions.find(i => i.sk === this.versionSelectedId)};
    if(selectedVersion.sk) {
      console.log('selectedVersion->', selectedVersion)
      this.store.dispatch(getSelectedVersionSuccess({ version: selectedVersion }));
    } else {
      this.store.dispatch(getSelectedVersionSuccess({ version: null }));
      this.elementSelected = this.formSelected;
    }
  }

  initForms() {
    this.store.dispatch(loadFormsList());    

    // Selectors
    this.store
      .pipe(select(setNewForm))
      .pipe(
        tap((isNewForm) => {
          this.isNewForm = isNewForm;
          this.viewSelected = "1";
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    this.store
      .pipe(select(setForms))
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (v) => {
          this.forms = v;
          if(!this.formSelected && v?.length > 0) {
            this.selectForm(v[0]);
          }    
        },
        error: (e) => {
          console.log('ERROR->', <any>e);
        }
      });

    this.store
      .pipe(select(setSelectedForm))
      .pipe(
        //filter(x => x.status == "success"),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (v) => {
          this.formSelected = v;
          if(v) {
            this.elementSelected = v; 
            this.store.dispatch(loadTemplatesList({ formId: this.formSelected.pk })); // Load templates for form           
          }
          // As versions array comes in form update version state
          if(this.formSelected?.pk == 'form') {
            this.versionSelectedId = null;
            this.store.dispatch(loadVersionsListSuccess({ data: this.formSelected?.versions }));
            this.store.dispatch(getSelectedVersionSuccess({ version: null }));
          }
        },
        error: (e) => {         
          console.log('ERROR->', <any>e);
        }
      });

    this.store
      .pipe(select(setVersions))
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (v) => {
          this.versions = v;
        },
        error: (e) => {
          console.log('ERROR->', <any>e);
        }
    });

    this.store
      .pipe(select(setSelectedVersion))
      .pipe(
        //filter(x => x.status == "success"),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (v) => {
          this.versionSelected = v;
          if(v) {
            this.elementSelected = v;
            this.versionSelectedId = v.sk;
          } else {
            this.versionSelectedId = null;
          }   
        },
        error: (e) => {         
          console.log('ERROR->', <any>e);
        }
      });

    this.route.queryParams.subscribe((params) => {    
      this.viewSelected = params?.tab || '1';
    });
  }

  newFormCanceled(event) {
    this.store.dispatch(newForm({ newForm: false }));
  }

  actionButtonNew() {
    this.store.dispatch(newForm({ newForm: true }));        
  }

  selectForm(form: any) {
    this.store.dispatch(getSelectedForm({ form }));
    this.store.dispatch(newForm({ newForm: false }));
    this.selectedViewParam();
  }
  
  searchSidebar(search: string) {
    this.search = search;
  }

  activeTab(tab: string) {
    if(tab !== this.viewSelected) {
      if(this.changes) {
        const dialogData: ConfirmationDialogData = {
          messagePrimary: 'You have unsaved changes.',
          messageSecondary: 'If proceeded these changes will be lost.',
          icon: 'error',
          confirmText: 'Proceed',
          cancelText: 'Cancel',
          showCancel: true
        };
    
        const dialogRef = this.dialog.open(ModalConfirmationComponent, {
          height: '250px',
          width: '500px',
          data: dialogData,
        });
    
        dialogRef.afterClosed().subscribe(result => {
          if (result === true) {
            // User clicked "Yes, Confirm"
            console.log('Confirmed!');
            this.changes = false;
            this.viewSelected = tab;
            this.selectedViewParam();
            this.store.dispatch(newForm({ newForm: false }));
            console.log('-tab->', tab)
            console.log('-type->', this.elementSelected.pk)
            console.log('-versions->', this.versions?.length)
            if(tab == '4' && this.elementSelected.pk != 'version' && this.versions?.length > 0) {
              this.versionSelectedId = this.versions[0].sk;
              this.selectVersion();
            }
          } else {
            // User clicked "Cancel" or closed the dialog
            console.log('Canceled or closed');
          }
        });
      } else {
        this.viewSelected = tab;
        this.selectedViewParam();
        this.store.dispatch(newForm({ newForm: false }));
        /* console.log('-tab->', tab)
        console.log('-type->', this.elementSelected.pk)
        console.log('-versions->', this.versions?.length)
        console.log('-versions id->', this.versions[0].sk) */
        if(tab == '4' && this.elementSelected.pk != 'version' && this.versions?.length > 0) {
          this.versionSelectedId = this.versions[0].sk;
          this.selectVersion();
        }
      }    
    }
  }

  changedDetected(changed: boolean) {
    this.changes = changed;
  }

  selectedViewParam() {
    this.router.navigate(['/dashboard'], {
      queryParams: {
        //id: this.formSelected.sk,
        tab: this.viewSelected,
      },
    });
  }

  deleteForm(form): void {

    const dialogData: ConfirmationDialogData = {
      messagePrimary: 'Do you really want to delete this Form?',
      messageSecondary: 'This process can not be undone.',
      icon: 'delete',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      showCancel: true
    };

    const dialogRef = this.dialog.open(ModalConfirmationComponent, {
      height: '250px',
      width: '500px',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        // User clicked "Yes, Confirm"
        console.log('Confirmed!');
        this.store.dispatch(deleteForm({ form: form }))
      } else {
        // User clicked "Cancel" or closed the dialog
        console.log('Canceled or closed');
      }
    });
  }

  initLoadingStatus() {
    // All status states are separated because future flexibility showing spinner and loaders in interface
    // Template loading status
    // this.store
    //   .pipe(select(setListStatusTemplate))
    //   .pipe(
    //     filter(x => x != "pending"),
    //     takeUntil(this.unsubscribe$)
    //   )
    //   .subscribe({
    //     next: (v) => {          
    //       if(v == "loading") {
    //         this.loading = true;
    //       } else {
    //         this.loading = false;
    //       }
    //     },
    //     error: (e) => {
    //       this.loading = false;
    //       console.log('ERROR->', <any>e);
    //     }
    //   });
    
    // Form loading status
    this.store
      .pipe(select(setListStatusForm))
      .pipe(
        filter(x => x != "pending"),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (v) => {          
          if(v == "loading") {
            this.loading = true;
          } else {
            this.loading = false;
          }
        },
        error: (e) => {
          this.loading = false;
          console.log('ERROR->', <any>e);
        }
      });

    this.store
      .pipe(select(setGetStatusForm))
      .pipe(
        filter(x => x != "pending"),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (v) => {          
          if(v == "loading") {
            this.loading = true;
          } else {
            this.loading = false;
          }
        },
        error: (e) => {
          this.loading = false;
          console.log('ERROR->', <any>e);
        }
    });

    this.store
      .pipe(select(setDeleteStatusForm))
      .pipe(
        filter(x => x != "pending"),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (v) => {          
          if(v == "loading") {
            this.loading = true;
          } else {
            this.loading = false;
          }
        },
        error: (e) => {
          this.loading = false;
          console.log('ERROR->', <any>e);
        }
    });

    this.store
      .pipe(select(setCreateStatusForm))
      .pipe(
        filter(x => x != "pending"),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (v) => {          
          if(v == "loading") {
            this.loading = true;
          } else {
            this.loading = false;
          }
        },
        error: (e) => {
          this.loading = false;
          console.log('ERROR->', <any>e);
        }
    });

    this.store
      .pipe(select(setUpdateStatusForm))
      .pipe(
        filter(x => x != "pending"),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (v) => {          
          if(v == "loading") {
            this.loading = true;
          } else {
            this.loading = false;
          }
        },
        error: (e) => {
          this.loading = false;
          console.log('ERROR->', <any>e);
        }
    });

    // Version loading status
    this.store
      .pipe(select(setCreateStatusVersion))
      .pipe(
        filter(x => x != "pending"),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (v) => {          
          if(v == "loading") {
            this.loading = true;
          } else {
            this.loading = false;
          }          
        },
        error: (e) => {
          this.loading = false;
          console.log('ERROR->', <any>e);
        }
      });

    this.store
      .pipe(select(setDeleteStatusVersion))
      .pipe(
        filter(x => x != "pending"),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (v) => {          
          if(v == "loading") {
            this.loading = true;
          } else {
            this.loading = false;
          }
          // After delete version success back to form
          if(v == "success") {
            this.store.dispatch(getSelectedVersionSuccess({ version: null }));
            this.elementSelected = this.formSelected;
          }
        },
        error: (e) => {
          this.loading = false;
          console.log('ERROR->', <any>e);
        }
    });

    this.store
      .pipe(select(setUpdateStatusVersion))
      .pipe(
        filter(x => x != "pending"),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (v) => {          
          if(v == "loading") {
            this.loading = true;
          } else {
            this.loading = false;
          }
        },
        error: (e) => {
          this.loading = false;
          console.log('ERROR->', <any>e);
        }
    });

    // Value loading status
    this.store
      .pipe(select(setListStatusValue))
      .pipe(
        filter(x => x != "pending"),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (v) => {        
          console.log('ENTRO --> ', v)
          setTimeout(() => {
            if(v == "loading") {
              this.loading = true;
            } else {
              this.loading = false;
            } 
          }, 0)                   
        },
        error: (e) => {
          this.loading = false;
          console.log('ERROR->', <any>e);
        }
      });

      this.store
      .pipe(select(setGetStatusValue))
      .pipe(
        filter(x => x != "pending"),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (v) => {          
          if(v == "loading") {
            this.loading = true;
          } else {
            this.loading = false;
          }          
        },
        error: (e) => {
          this.loading = false;
          console.log('ERROR->', <any>e);
        }
      });

      // Clone loading status
    this.store
    .pipe(select(setCloneStatusForm))
    .pipe(
      filter(x => x != "pending"),
      takeUntil(this.unsubscribe$)
    )
    .subscribe({
      next: (v) => {          
        if(v == "loading") {
          this.loading = true;
        } else {
          this.loading = false;
        }          
      },
      error: (e) => {
        this.loading = false;
        console.log('ERROR->', <any>e);
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
